// src/components/MyDiffsonas.ts
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getMyDiffsonas, type MyDiffsonaItem } from '../client/ApiClient';
import '../components/ShareModal';
import { CDN_CONTENT_ENDPOINT } from '../constants';

@customElement('my-diffsonas')
export class MyDiffsonas extends LitElement {
  createRenderRoot() { return this; }

  @property() mode: 'view' | 'select' = 'view';
  @property() selectedSlug: string = '';

  @state() private items: MyDiffsonaItem[] = [];
  @state() private loading = true;
  @state() private error: string | null = null;
  @state() private unauthorized = false;

  @state() private idx: Record<string, number> = {};
  // NEW: which starter path the single card should use
  @state() private startMode: 'images' | 'description' = 'description';
  @state() private canScrollLeft = false;
  @state() private canScrollRight = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.load();
  }

  private async load() {
    this.loading = true; this.error = null; this.unauthorized = false;
    try {
      const items = await getMyDiffsonas(12, 6);
      this.items = items || [];
      const idx: Record<string, number> = {};
      for (const it of this.items) idx[it.slug] = 0;
      this.idx = idx;
    } catch (e: any) {
      const msg = e?.message || '';
      if (/unauthorized|forbidden|401/i.test(msg)) {
        this.unauthorized = true;
        this.items = [];
      } else {
        this.error = msg || 'Failed to load diffsonas';
      }
    } finally {
      this.loading = false;
      this.dispatchEvent(new CustomEvent('my-diffsonas-loaded', {
        detail: { count: this.items.length, items: this.items },
        bubbles: true, composed: true
      }));
    }
  }

  /** If logged out, open auth modal + remember redirect; otherwise navigate. */
  private goOrAuth(path: string) {
    if (this.unauthorized) {
      try { localStorage.setItem('post_login_redirect', path); } catch {}
      this.dispatchEvent(new CustomEvent('auth-required', { bubbles: true, composed: true }));
      return;
    }
    window.location.href = path;
  }

  /** Emit event when a diffsona is selected (in select mode) */
  private selectDiffsona(slug: string, name: string) {
    this.dispatchEvent(new CustomEvent('diffsona-selected', {
      detail: { slug, name },
      bubbles: true,
      composed: true
    }));

    // Emit scroll event to trigger IndexPage to scroll to create section
    setTimeout(() => {
      this.dispatchEvent(new CustomEvent('scroll-to-create', {
        bubbles: true,
        composed: true
      }));
    }, 100);
  }

  private setIndex(slug: string, i: number) {
    const it = this.items.find(x => x.slug === slug);
    if (!it || it.images.length === 0) return;
    const n = ((i % it.images.length) + it.images.length) % it.images.length;
    this.idx = { ...this.idx, [slug]: n };
  }
  private next(slug: string) { this.setIndex(slug, (this.idx[slug] ?? 0) + 1); }
  private prev(slug: string) { this.setIndex(slug, (this.idx[slug] ?? 0) - 1); }

  private scrollCarousel(direction: 'left' | 'right') {
    const container = this.querySelector('#diffsonas-carousel') as HTMLElement;
    if (!container) return;

    const scrollAmount = 332; // Card width (320px) + gap (12px)
    const targetScroll = direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({ left: targetScroll, behavior: 'smooth' });
  }

  private updateScrollButtons() {
    const container = this.querySelector('#diffsonas-carousel') as HTMLElement;
    if (!container) return;

    this.canScrollLeft = container.scrollLeft > 0;
    this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 10);
  }

  firstUpdated() {
    const container = this.querySelector('#diffsonas-carousel') as HTMLElement;
    if (container) {
      container.addEventListener('scroll', () => this.updateScrollButtons());
      // Initial check
      setTimeout(() => this.updateScrollButtons(), 100);
    }
  }

  private firstCreateCard() {
    const toPath = this.startMode === 'images'
      ? '/diffsona-from-images'
      : '/diffsona-from-description';

    const desc = this.startMode === 'images'
      ? `Upload a few reference photos. Diffsona learns your look for consistent generations.`
      : `Describe style, vibe, clothes, and constraints. We’ll guide you with presets.`;

    const isImages = this.startMode === 'images';
    const ctaDisabled = isImages; // disable until images flow is ready
    const ctaLabel = isImages ? 'Coming Soon' : 'Create your Diffsona';

    return html`
      <article
        class="relative overflow-hidden rounded-3xl bg-slate-800/60 border border-white/10 shadow-xl backdrop-blur-md p-6 sm:p-8"
      >
        <div class="pointer-events-none absolute -inset-1 opacity-0 blur-2xl transition-opacity duration-300"
             style="background: radial-gradient(60% 60% at 50% 0%, rgba(99,102,241,.35) 0%, rgba(14,165,233,.25) 35%, transparent 70%);"></div>

        <div class="relative grid sm:grid-cols-2 gap-6 items-center">
          <!-- Left: vertical preview image -->
          <div class="rounded-2xl overflow-hidden border border-white/10 bg-slate-900/50">
            <img src="/static/images/my-diffsonas-empty.webp"
                 alt="Placeholder preview"
                 class="w-full h-full object-cover object-top opacity-90"
                 loading="lazy" />
          </div>

          <!-- Right: welcome + description + CTA -->
          <div class="flex flex-col h-full ">
            <header class="space-y-3">
              <h3 class="text-xl sm:text-2xl font-semibold text-white">Welcome to Diffsona</h3>
              <p class="text-slate-300/90 text-sm leading-relaxed">
                Build your very first Diffsona character and start creating unique images.
              </p>
              <p class="text-slate-300/90 text-sm leading-relaxed">
                Choose whether you’d like to begin with a few reference photos or just a description.
              </p>
            </header>

            <!-- BIG mode toggle -->
            <div class="mt-5 hidden rounded-2xl border border-white/10 bg-white/5 p-1 w-full max-w-xl">
              <button
                class="flex-1 px-4 py-2 rounded-xl text-base md:text-lg font-medium cursor-pointer
                       ${this.startMode === 'description' ? 'bg-indigo-500/40 text-white' : 'hover:bg-white/5'}"
                aria-pressed=${this.startMode === 'description' ? 'true' : 'false'}
                @click=${() => (this.startMode = 'description')}
              >
                From Description
              </button>
              <button
                class="flex-1 px-4 py-2 rounded-xl text-base md:text-lg font-medium cursor-pointer
                       ${this.startMode === 'images' ? 'bg-white/10 text-white' : 'hover:bg-white/5'}"
                aria-pressed=${this.startMode === 'images' ? 'true' : 'false'}
                @click=${() => (this.startMode = 'images')}
              >
                From Images
              </button>
            </div>

            <!-- Contextual description -->
            <p class="mt-4 text-slate-300/90 leading-relaxed">${desc}</p>

            <!-- CTA -->
            <div class="mt-6 flex flex-col items-center gap-2">
              <button
                class="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-base font-semibold
                       ${ctaDisabled
                          ? 'bg-white/10 text-slate-300 cursor-not-allowed opacity-60'
                          : 'bg-indigo-500 hover:bg-indigo-600 text-white shadow cursor-pointer'}"
                ?disabled=${ctaDisabled}
                title=${ctaDisabled ? 'Coming Soon' : 'Create'}
                @click=${() => { if (!ctaDisabled) this.goOrAuth(toPath); }}
              >
                ${ctaLabel}
                ${!ctaDisabled ? html`
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path fill-rule="evenodd"
                          d="M4.5 12a.75.75 0 0 1 .75-.75h11.69l-4.72-4.72a.75.75 0 1 1 1.06-1.06l6 6a.75.75 0 0 1 0 1.06l-6 6a.75.75 0 1 1-1.06-1.06l4.72-4.72H5.25A.75.75 0 0 1 4.5 12Z"
                          clip-rule="evenodd"/>
                  </svg>
                ` : null}
              </button>

              ${ctaDisabled ? html`
                <div class="text-xs text-slate-400">
                  Image-based onboarding is on the way — try <span class="text-slate-200">From Description</span> to start now.
                </div>
              ` : null}
            </div>
          </div>
        </div>
      </article>
    `;
  }

  private card(item: MyDiffsonaItem) {
    const slug = encodeURIComponent(item.slug);
    const images = item.images || [];
    const current = images[(this.idx[item.slug] ?? 0)] || images[0] || null;
    const isSelected = this.selectedSlug === item.slug;

    return html`
      <article class="relative overflow-hidden rounded-2xl border ${isSelected ? 'border-indigo-400/80 shadow-xl shadow-indigo-500/40' : 'border-white/10'} bg-slate-900/40
                      w-80 flex-shrink-0 transition-all duration-300 ease-out
                      ${isSelected ? 'scale-105' : 'hover:border-indigo-400/40 hover:shadow-lg hover:shadow-indigo-500/20 hover:scale-105'}
                      active:scale-95">
        <div class="p-2.5">
          <h3 class="text-xs font-semibold text-white mb-1">${item.name}</h3>

          ${this.mode === 'select' ? html`
            <button
              class="group/btn relative text-center rounded-xl block border ${isSelected ? 'border-emerald-400/60 bg-emerald-500/30' : 'border-indigo-400/40 bg-indigo-500/30'}
                     text-white font-semibold ${isSelected ? 'hover:bg-emerald-500/40' : 'hover:bg-indigo-500/50'}
                     px-4 py-1.5 w-full transition-all duration-300 overflow-hidden cursor-pointer mb-2"
              @click=${() => this.selectDiffsona(item.slug, item.name)}>
              <span class="relative z-10 inline-flex items-center justify-center gap-2 text-sm">
                ${isSelected ? html`
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 13l4 4L19 7"/>
                  </svg>
                  Selected
                ` : html`
                  Select
                  <svg class="h-3.5 w-3.5 transition-transform duration-300 group-hover/btn:translate-x-1"
                       fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                `}
              </span>
              <span class="absolute inset-0 bg-gradient-to-r ${isSelected ? 'from-emerald-500/0 via-emerald-400/30 to-emerald-500/0' : 'from-indigo-500/0 via-indigo-400/30 to-indigo-500/0'}
                           translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700"></span>
            </button>
          ` : html`
            <a href=${`/diffsona/${slug}/create`}
               class="group/btn relative text-center rounded-xl block border border-indigo-400/40
                      text-white font-semibold bg-indigo-500/30 hover:bg-indigo-500/50
                      px-4 py-1.5 w-full transition-all duration-300 overflow-hidden mb-2">
              <span class="relative z-10 inline-flex items-center justify-center gap-2 text-sm">
                Use
                <svg class="h-3.5 w-3.5 transition-transform duration-300 group-hover/btn:translate-x-1"
                     fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </span>
              <span class="absolute inset-0 bg-gradient-to-r from-indigo-500/0 via-indigo-400/30 to-indigo-500/0
                           translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700"></span>
            </a>
          `}

          ${images.length > 0 ? html`
            <div class="grid grid-cols-6 gap-1">
              ${images.slice(0, 6).map((u, i) => html`
                <button type="button" class="relative rounded overflow-hidden border ${i === (this.idx[item.slug] ?? 0) ? 'border-indigo-400/60' : 'border-white/10'}
                                                       hover:border-indigo-400/40 transition-colors"
                  @click=${() => this.setIndex(item.slug, i)}>
                  <img src=${CDN_CONTENT_ENDPOINT}${u} class="h-12 w-full object-cover" loading="lazy" />
                </button>
              `)}
            </div>
          ` : html`
            <div class="rounded-lg bg-slate-800/60 p-2.5 text-center text-slate-400 text-xs">
            </div>
          `}
        </div>
      </article>
    `;
  }

  private newCard() {
    return html`
      <article class="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/40
                      w-80 flex-shrink-0 transition-all duration-300 ease-out
                      hover:border-indigo-400/40 hover:shadow-lg hover:shadow-indigo-500/20 hover:scale-105
                      active:scale-95">
        <div class="p-2.5 h-full flex flex-col items-center justify-center ">
          <button
            class="group/btn relative text-center rounded-xl block border border-indigo-400/40
                   text-white font-semibold bg-indigo-500/30 hover:bg-indigo-500/50
                   px-4 py-1.5 w-full transition-all duration-300 overflow-hidden cursor-pointer"
            @click=${() => this.dispatchEvent(new CustomEvent('create-new-diffsona', { bubbles: true, composed: true }))}>
            <span class="relative z-10 inline-flex items-center justify-center gap-2 text-sm">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              New Diffsona
            </span>
            <span class="absolute inset-0 bg-gradient-to-r from-indigo-500/0 via-indigo-400/30 to-indigo-500/0
                         translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700"></span>
          </button>
          <div class="mt-3 text-xs text-slate-400 text-center">
            Create a new character
          </div>
        </div>
      </article>
    `;
  }

  render() {
    // Simplified "empty" state: one card with a toggle & single CTA
    const emptyCreate = html`
      <div class="">
        ${this.firstCreateCard()}
      </div>
    `;

    return html`
      <section class="">

        ${this.error ? html`
          <div class="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">${this.error}</div>
        ` : this.loading ? html`
          <div id="diffsonas-carousel" class="relative overflow-x-auto scrollbar-thin scrollbar-thumb-indigo-500/50 scrollbar-track-slate-800/30">
            <div class="flex gap-3 pb-1">
              ${Array.from({ length: 8 }).map(() => html`
                <div class="rounded-2xl border border-white/10 bg-slate-900/40 p-2 w-64 flex-shrink-0">
                  <div class="h-44 w-full rounded-lg bg-slate-800/80 animate-pulse"></div>
                  <div class="mt-2 h-3 w-24 bg-slate-800 rounded animate-pulse"></div>
                </div>
              `)}
            </div>
          </div>
        ` : html`
          <div class="relative group">
            <!-- Left Navigation Arrow -->
            ${this.canScrollLeft ? html`
              <button
                class="absolute left-2 top-1/2 -translate-y-1/2 z-10
                       rounded-full bg-gradient-to-r from-indigo-500/40 to-purple-500/30
                       border border-indigo-400/30 backdrop-blur-md
                       p-3 text-white shadow-lg shadow-indigo-500/20
                       hover:from-indigo-500/60 hover:to-purple-500/50 hover:border-indigo-400/60
                       hover:scale-110 active:scale-95
                       transition-all duration-300 ease-out
                       opacity-0 group-hover:opacity-100"
                @click=${() => this.scrollCarousel('left')}
                aria-label="Scroll left"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              </button>
            ` : null}

            <!-- Carousel Container -->
            <div id="diffsonas-carousel" class="overflow-x-auto scrollbar-thin scrollbar-thumb-indigo-500/50 scrollbar-track-slate-800/30 scroll-smooth">
              <div class="flex gap-3 pb-1">
                ${this.items.map((it) => this.card(it))}
                ${this.newCard()}
              </div>
            </div>

            <!-- Right Navigation Arrow -->
            ${this.canScrollRight ? html`
              <button
                class="absolute right-2 top-1/2 -translate-y-1/2 z-10
                       rounded-full bg-gradient-to-r from-indigo-500/40 to-purple-500/30
                       border border-indigo-400/30 backdrop-blur-md
                       p-3 text-white shadow-lg shadow-indigo-500/20
                       hover:from-indigo-500/60 hover:to-purple-500/50 hover:border-indigo-400/60
                       hover:scale-110 active:scale-95
                       transition-all duration-300 ease-out
                       opacity-0 group-hover:opacity-100"
                @click=${() => this.scrollCarousel('right')}
                aria-label="Scroll right"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            ` : null}
          </div>
        `}
      </section>
    `;
  }
}

